import { useState, useRef, useEffect } from "react";
import { useApi } from "../hooks/useApi";
import { SUBSIDIES } from "../data/subsidies";

const SYSTEM_PROMPT = `あなたは農援の補助金診断AIです。農業補助金に精通した行政書士・経営コンサルタントの視点で農家の状況をヒアリングし、申請できる可能性がある補助金を提案します。

【農援のポジション】申請代行はしない。農家の状況を整理し補助金をマッチング、最終的な申請相談は提携行政書士へつなぐ。農協が案内しない補助金も含めて農家ファーストで情報提供。

【ヒアリング順序】「わからない・大体」でも受け入れ次へ進む。
①都道府県・市区町村 ②個人/法人 ③作目 ④やりたいこと・投資金額 ⑤契約・購入していないか（重要）
余裕があれば確認：⑥認定農業者か ⑦青色申告か ⑧見積書の有無 ⑨農業経験年数 ⑩いつまでに必要か

【採択率向上ヒアリング】「現在この作業に年間何時間かかりますか？」「導入後いくら売上を増やしたいですか？」など数字で事業計画の核心を引き出す。

【絶対確認事項】契約・発注・購入済みは事前着手でアウト。同一経費の重複申請不可。消費税・中古品・リースは対象外が多い。認定農業者・認定新規就農者の資格が必要な補助金多数。

【補助金名の書き方】具体的な制度を挙げるときは、できるだけ正式名称で書くこと（例：「農業次世代人材投資資金」「強い農業・担い手づくり総合支援交付金」「経営所得安定対策（ゲタ・ナラシ対策）」）。正式名称で書くと、農家さんが公式ページをすぐ開けるようになります。

【表現ルール厳守】
NG：「使えます」「採択されます」「申請できます」「対象です」
OK：「申請できる可能性があります」「対象になる可能性があります」「最終判断は行政書士・事務局への確認が必要です」

【回答スタイル】短く会話調。専門用語は平易に言い換え（認定農業者→「市役所に農業計画を出して認定を受けた農家さん」）。具体的な次のアクションを提示。相談を進めたい場合は「画面右上の相談シートをコピーして行政書士へ」と案内。`;

const EXAMPLE_CHATS = [
  { label: "🚜 古いトラクターを買い替えたい。お金の助けはある？", msg: "古いトラクターを買い替えたいんですが、半額くらい補助が出る制度ってありますか？" },
  { label: "🌱 農業を始めたばかりでお金が足りない", msg: "就農して2年目なんですが、機械を買うお金が足りなくて困っています。何か支援はありますか？" },
  { label: "😟 農協に相談したら難しいと言われた", msg: "農協に補助金の相談をしたら「条件が厳しい」と言われてしまいました。他に方法はありますか？" },
  { label: "🌿 農薬を減らして有機農業にしたい", msg: "少しずつ農薬を減らして有機農業に転換したいと思っています。支援はありますか？" },
  { label: "💰 天候で収入が不安定。リスクを減らしたい", msg: "毎年、台風や天候不順で収入がバラバラで不安です。何か安定させる制度はありますか？" },
];

// ── 補助金名 → 公式ページURL の自動リンク化テーブル ──
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const findUrl = (kw) => (SUBSIDIES.find((s) => s.name.includes(kw) && s.applyUrl) || {}).applyUrl;
const ALIAS_KEYWORDS = [
  "ゲタ・ナラシ", "スーパーL資金", "農業次世代人材投資資金", "次世代人材投資資金",
  "強い農業・担い手づくり総合支援交付金", "有機農業", "収入保険", "青年等就農資金",
];
const LINKS = [
  ...SUBSIDIES.filter((s) => s.applyUrl).map((s) => ({ key: s.name, url: s.applyUrl })),
  ...ALIAS_KEYWORDS.map((kw) => ({ key: kw, url: findUrl(kw) })).filter((x) => x.url),
].sort((a, b) => b.key.length - a.key.length);
const LINK_RE = LINKS.length
  ? new RegExp("(" + LINKS.map((l) => escapeRe(l.key)).join("|") + ")", "g")
  : null;
const urlOf = (k) => (LINKS.find((l) => l.key === k) || {}).url;

// 1行を、補助金名はリンクにして描画
function RichLine({ text }) {
  if (!text) return null;
  if (!LINK_RE) return <>{text}</>;
  const parts = text.split(LINK_RE);
  return (
    <>
      {parts.map((p, i) =>
        urlOf(p) ? (
          <a key={i} href={urlOf(p)} target="_blank" rel="noreferrer"
             style={{ color: "#1a6a37", fontWeight: 700, textDecoration: "underline" }}>
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

export default function ChatScreen({ initMessage, onClear }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "こんにちは！農援のAI相談窓口です🌾\n\n「補助金って何？」という方も大歓迎です。農業でやりたいこと・困っていることを気軽に話してください。一緒に考えます。\n\n💬 どんなことでも聞いてください。",
    },
  ]);
  const [started, setStarted] = useState(false);
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);
  const { callStream, loading } = useApi();
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ホーム画面からの初期メッセージを自動送信
  useEffect(() => {
    if (initMessage && !started) {
      setStarted(true);
      onClear && onClear();
      send(initMessage);
    }
  }, [initMessage]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const base = [...messages, { role: "user", content: msg }];
    // 先に空のAI吹き出しを置き、ストリーミングで埋めていく
    setMessages([...base, { role: "assistant", content: "", streaming: true }]);
    const recent = base.slice(-8).map((m) => ({ role: m.role, content: m.content }));

    const full = await callStream({
      system: SYSTEM_PROMPT,
      messages: recent,
      maxTokens: 600,
      onToken: (t) =>
        setMessages((cur) => {
          const copy = cur.slice();
          copy[copy.length - 1] = { role: "assistant", content: t, streaming: true };
          return copy;
        }),
    });

    setMessages((cur) => {
      const copy = cur.slice();
      copy[copy.length - 1] = { role: "assistant", content: full, streaming: false };
      return copy;
    });
  };

  // 行政書士引き継ぎシートのコピー
  const copySheet = () => {
    const sheet = [
      "■ 農援 行政書士引き継ぎシート",
      `生成日時: ${new Date().toLocaleString("ja-JP")}`,
      "",
      "【農家さんとのチャット内容】",
      ...messages.map(m => `${m.role === "user" ? "農家さん" : "農援AI"}: ${m.content}`),
      "",
      "※ 最終判断は行政書士または各事務局にご確認ください。",
    ].join("\n");
    navigator.clipboard.writeText(sheet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="chat-wrap">
      {/* 引き継ぎシートボタン */}
      {messages.length > 2 && (
        <div style={{ padding:"0.5rem 1rem 0", textAlign:"right" }}>
          <button
            onClick={copySheet}
            style={{ fontSize:"0.75rem", background: copied ? "#2e7d32" : "#e8f5e9", color: copied ? "#fff" : "#2e7d32", border:"1px solid #2e7d32", borderRadius:99, padding:"0.3rem 0.8rem", cursor:"pointer", fontWeight:600 }}
          >
            {copied ? "✅ コピーしました" : "📋 相談シートをコピー（行政書士へ）"}
          </button>
        </div>
      )}

      <div className="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`msg-row ${m.role}`}>
            {m.role === "assistant" && <span className="avatar">🌾</span>}
            <div className={`bubble ${m.role}`}>
              {m.role === "assistant" && m.streaming && m.content === "" ? (
                <span className="typing">考え中...</span>
              ) : (
                <>
                  {m.content.split("\n").map((l, j) => (
                    <span key={j}><RichLine text={l} /><br /></span>
                  ))}
                  {m.streaming && <span style={{ opacity:0.5 }}>▍</span>}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* 会話例（メッセージがAIの最初の1件だけのとき表示） */}
      {messages.length <= 1 && (
        <div style={{ padding:"0 1rem 0.5rem" }}>
          <div style={{ fontSize:"0.75rem", color:"#888", marginBottom:"0.5rem", fontWeight:600 }}>
            💬 こんな感じで話しかけてみてください
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"0.4rem" }}>
            {EXAMPLE_CHATS.map((e) => (
              <button
                key={e.msg}
                onClick={() => send(e.msg)}
                style={{ textAlign:"left", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"0.65rem 0.85rem", fontSize:"0.83rem", color:"#374151", cursor:"pointer", lineHeight:1.5 }}
              >
                {e.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 入力欄（複数行対応） */}
      <div className="input-row" style={{ alignItems:"flex-end" }}>
        <textarea
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && e.shiftKey && send()}
          placeholder={"なんでも気軽に話しかけてください...\n（Shift+Enterで送信）"}
          rows={2}
          style={{ resize:"none", lineHeight:1.5, padding:"0.6rem 0.75rem", borderRadius:12, fontSize:"0.95rem" }}
        />
        <button className="send-btn" onClick={() => send()} disabled={loading}>
          送信
        </button>
      </div>
    </div>
  );
}
